import React, { useState, useCallback, useEffect } from 'react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { Undo2, ExternalLink } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { fetchHistory, restoreInvoice } from '../../lib/api';

const GHL_LOCATION_ID = '2llwOnTMGXnzyMKidM7j';

const STATUS_CONFIG = {
  approved: {
    label: 'Approved',
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    row: 'bg-emerald-50/40',
  },
  merged: {
    label: 'Merged',
    badge: 'bg-blue-50 text-blue-700 border-blue-200',
    row: 'bg-blue-50/40',
  },
  rejected: {
    label: 'Rejected',
    badge: 'bg-red-50 text-red-700 border-red-200',
    row: 'bg-red-50/40',
  },
};

export default function HistoryTable({ onRestored }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [restoringId, setRestoringId] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetchHistory(filter);
      setItems(res.data.data || []);
    } catch (err) {
      toast.error('Failed to load history');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const handleRestore = async (id) => {
    setRestoringId(id);
    try {
      await restoreInvoice(id);
      setItems(prev => prev.filter(i => i.id !== id));
      toast.success('Restored to pending queue');
      if (onRestored) onRestored();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to restore';
      toast.error(msg);
    } finally {
      setRestoringId(null);
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    try { return format(new Date(d), 'MM/dd/yyyy'); } catch { return d; }
  };

  const formatCurrency = (a) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(parseFloat(a) || 0);

  const cfg = (status) => STATUS_CONFIG[status] || STATUS_CONFIG.rejected;

  return (
    <div className="space-y-3">
      {/* Filter row */}
      <div className="flex items-center justify-between px-4 sm:px-6">
        <span className="text-xs text-muted-foreground">{items.length} record{items.length !== 1 ? 's' : ''}</span>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="history-filter">
            <SelectValue placeholder="Filter" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All History</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="sent">Sent (Pending Payment)</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="merged">Merged</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="p-6 space-y-3">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="p-10 text-center">
          <p className="text-sm text-muted-foreground">No history yet</p>
        </div>
      ) : (
        <div className="overflow-x-auto" data-testid="history-table">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-[110px] text-xs font-semibold uppercase tracking-wide text-muted-foreground">Date</TableHead>
                <TableHead className="min-w-[120px] text-xs font-semibold uppercase tracking-wide text-muted-foreground">Client</TableHead>
                <TableHead className="min-w-[100px] text-xs font-semibold uppercase tracking-wide text-muted-foreground">Staff</TableHead>
                <TableHead className="w-[100px] text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">Amount</TableHead>
                <TableHead className="min-w-[280px] text-xs font-semibold uppercase tracking-wide text-muted-foreground">Description</TableHead>
                <TableHead className="w-[110px] text-xs font-semibold uppercase tracking-wide text-muted-foreground">Outcome</TableHead>
                <TableHead className="w-[70px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((inv) => {
                const sc = cfg(inv.status);
                const staffNames = Array.isArray(inv.staff_data)
                  ? inv.staff_data.map(s => s.username || s.email || '').filter(Boolean).join(', ')
                  : '';

                return (
                  <TableRow key={inv.id} className={`${sc.row} transition-colors duration-150`}>
                    <TableCell>
                      <span className="text-sm" data-role="mono">{formatDate(inv.updated_at || inv.created_at)}</span>
                    </TableCell>
                    <TableCell>
                      {inv.client ? (
                        inv.clickup_task_id && !inv.clickup_task_id.startsWith('merged_') ? (
                          <a
                            href={`https://app.clickup.com/t/${inv.clickup_task_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm font-medium text-primary hover:underline truncate block max-w-[160px]"
                            title={`${inv.client} — Open in ClickUp`}
                          >
                            {inv.client}
                          </a>
                        ) : (
                          <span className="text-sm font-medium truncate block max-w-[160px]">{inv.client}</span>
                        )
                      ) : <em className="text-sm text-muted-foreground">—</em>}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm truncate block max-w-[120px]" title={staffNames}>
                        {staffNames || <em className="text-muted-foreground">—</em>}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      {(() => {
                        const disc = inv.discount || {};
                        const hasDisc = disc.type && disc.type !== 'none' && parseFloat(disc.amount) > 0;
                        if (hasDisc) {
                          const origAmount = disc.original_amount
                            ? parseFloat(disc.original_amount)
                            : parseFloat(inv.hours || 0) * parseFloat(inv.rate || 195);
                          return (
                            <div className="text-right">
                              <span className="text-xs text-red-500 line-through block" data-role="mono">{formatCurrency(origAmount)}</span>
                              <span className="text-sm font-semibold block" data-role="mono">{formatCurrency(inv.amount)}</span>
                              <span className="text-[10px] text-muted-foreground block" data-role="mono">-{formatCurrency(disc.amount)}</span>
                            </div>
                          );
                        }
                        return <span className="text-sm font-medium" data-role="mono">{formatCurrency(inv.amount)}</span>;
                      })()}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm leading-relaxed line-clamp-2">{inv.description || '—'}</span>
                    </TableCell>
                    <TableCell>
                      {inv.status === 'approved' && inv.ghl_invoice_id ? (
                        <a
                          href={`https://app.leadseverywhere.net/v2/location/${GHL_LOCATION_ID}/payments/invoices/${inv.ghl_invoice_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1"
                        >
                          <Badge variant="outline" className={`${sc.badge} text-xs cursor-pointer hover:opacity-80`}>
                            {sc.label}
                            <ExternalLink className="h-3 w-3 ml-1" />
                          </Badge>
                        </a>
                      ) : (
                        <Badge variant="outline" className={`${sc.badge} text-xs`}>
                          {sc.label}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {inv.status !== 'approved' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                          onClick={() => handleRestore(inv.id)}
                          disabled={restoringId === inv.id}
                          title="Restore to pending"
                          data-testid="history-restore-button"
                        >
                          <Undo2 className={`h-3.5 w-3.5 ${restoringId === inv.id ? 'animate-spin' : ''}`} />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
